import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);
import { API } from "@/lib/apiBase";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If URL has session_id, AuthCallback will handle auth
    const fullUrl = window.location.href || "";
    const hash = window.location.hash || "";
    if (hash.includes("session_id=") || fullUrl.includes("session_id=")) {
      setLoading(false);
      setUser(null);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    // Fetch full user data with subscription
    const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
    setUser(meRes.data);
    return meRes.data;
  };

  const register = async (name, email, password) => {
    await axios.post(`${API}/auth/register`, { name, email, password }, { withCredentials: true });
    const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
    setUser(meRes.data);
    return meRes.data;
  };

  const googleLogin = async (sessionId) => {
    // Session endpoint returns full user + subscription in one response (no race with Set-Cookie)
    const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId }, { withCredentials: true });
    setUser(res.data);
    // Defensive: re-fetch after a short delay to confirm cookies are committed.
    // If this fails, the first response already set the user state, so login still succeeds.
    setTimeout(async () => {
      try {
        const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(meRes.data);
      } catch {
        // Non-fatal: cookies may not be committed yet on the production domain.
        // The user object from the session response is already in state.
      }
    }, 300);
    return res.data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    setUser(false);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleLogin, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
